import type { SeedScenario } from './seedLatinScenarios'

/**
 * Scenarios for French, Spanish and Italian.
 *
 * Same three shapes as the German set — a party, a restaurant, a train — so
 * switching languages feels like the same app rather than three different ones.
 * Kept in their own file only because one scenario table for every language was
 * becoming unreadable; `seedLatinScenarios.ts` merges them.
 *
 * The register shifts on purpose. The party beats use tu/tú; the train beats use
 * vous, usted and Lei, which is what a stranger a generation older would
 * actually say. Your answers are first person either way, so the same island
 * fits both — but hearing the shift is half of learning when to use it.
 */
export const ROMANCE_SCENARIOS: Record<string, SeedScenario[]> = {
  fr: [
    {
      slug: 'soiree',
      title: 'Meeting someone at a party',
      nativeTitle: 'À une soirée',
      setting: "Paris, l'appartement d'un ami, vendredi soir",
      brief:
        "A friend's flat in Paris. The man by the kitchen table turns and starts talking. Three of your islands answer everything he is about to ask — the only question is whether you reach the right one before the silence lands.",
      beats: [
        {
          speaker: 'Julien',
          prompt: 'Bonsoir, moi c’est Julien. Tu fais quoi dans la vie ?',
          english: "Good evening, I'm Julien. What do you do for a living?",
          cue: 'Say what you do and how long you have done it.',
          island: 'fr_travail',
          blocks: ['fr_travail_b1', 'fr_travail_b2'],
          hint: 'depuis + present tense — French says "I work since five years".',
        },
        {
          speaker: 'Julien',
          prompt: 'Ah, intéressant. Tu parles bien français — tu apprends depuis longtemps ?',
          english: 'Interesting. You speak French well — have you been learning long?',
          cue: 'Give the length of time, then why you started.',
          island: 'fr_pourquoi',
          blocks: ['fr_pourquoi_b1', 'fr_pourquoi_b2'],
          hint: 'The compliment is the trigger. This island should be automatic.',
        },
        {
          speaker: 'Julien',
          prompt: 'Tu es venu seul ? Tu as de la famille ici ?',
          english: 'Did you come alone? Do you have family here?',
          cue: 'Account for your household.',
          island: 'fr_famille',
          blocks: ['fr_famille_b1', 'fr_famille_b2'],
        },
        {
          speaker: 'Julien',
          prompt: 'Et tu travailles au bureau ou chez toi ?',
          english: 'And do you work at the office or from home?',
          cue: 'Say where you usually work.',
          island: 'fr_travail',
          blocks: ['fr_travail_b4'],
        },
      ],
    },
    {
      slug: 'restaurant',
      title: 'Dinner for two',
      nativeTitle: 'Au restaurant',
      setting: 'Un bistrot plein, samedi soir, vingt heures trente',
      brief:
        'A packed bistro at half past eight. The waiter is moving fast and will not slow down for you. Every beat is a sentence you already have — this is a speed test, not a comprehension one.',
      beats: [
        {
          speaker: 'Serveur',
          prompt: 'Bonsoir. Vous êtes combien ?',
          english: 'Good evening. How many of you?',
          cue: 'Answer in one breath.',
          island: 'fr_restaurant',
          blocks: ['fr_restaurant_b1'],
        },
        {
          speaker: 'Serveur',
          prompt: 'Suivez-moi. Je vous laisse regarder.',
          english: 'Follow me. I will let you look.',
          cue: 'Stop him — you have nothing to look at yet.',
          island: 'fr_restaurant',
          blocks: ['fr_restaurant_b2'],
          hint: 'la carte is the menu; le menu is a set meal.',
        },
        {
          speaker: 'Serveur',
          prompt: 'Vous avez choisi ?',
          english: 'Have you decided?',
          cue: 'Order a dish.',
          island: 'fr_restaurant',
          blocks: ['fr_restaurant_b3'],
        },
        {
          speaker: 'Serveur',
          prompt: 'Très bien. Une préférence, une allergie ?',
          english: 'Very good. Any preference, any allergy?',
          cue: 'Leave something out.',
          island: 'fr_restaurant',
          blocks: ['fr_restaurant_b4'],
        },
        {
          speaker: 'Serveur',
          prompt: 'Ce sera tout ?',
          english: 'Will that be all?',
          cue: 'Close it out.',
          island: 'fr_restaurant',
          blocks: ['fr_restaurant_b5'],
        },
      ],
    },
    {
      slug: 'train',
      title: 'Small talk on the train',
      nativeTitle: 'Dans le train',
      setting: 'TGV pour Lyon, deux heures, la place en face se libère',
      brief:
        'Two hours to Lyon and the woman opposite has decided to talk. Nothing here is transactional — she is simply curious, so no script fits perfectly and you have to reach for the closest island quickly. Note she uses vous throughout.',
      beats: [
        {
          speaker: 'Madame Rousseau',
          prompt: 'Excusez-moi — vous apprenez le français ? Pourquoi donc ?',
          english: "Excuse me — you're learning French? Why, then?",
          cue: 'Say how it started.',
          island: 'fr_pourquoi',
          blocks: ['fr_pourquoi_b2'],
        },
        {
          speaker: 'Madame Rousseau',
          prompt: 'Et qu’est-ce qui vous amène à Lyon ? Le travail ?',
          english: 'And what brings you to Lyon? Work?',
          cue: 'Say what you do.',
          island: 'fr_travail',
          blocks: ['fr_travail_b1', 'fr_travail_b2'],
        },
        {
          speaker: 'Madame Rousseau',
          prompt: 'Vous avez de la famille en France ?',
          english: 'Do you have family in France?',
          cue: 'Account for your household.',
          island: 'fr_famille',
          blocks: ['fr_famille_b1', 'fr_famille_b2'],
        },
        {
          speaker: 'Madame Rousseau',
          prompt: 'Qu’est-ce qui vous semble le plus difficile ?',
          english: 'What seems hardest to you?',
          cue: 'Name the hard part.',
          island: 'fr_pourquoi',
          blocks: ['fr_pourquoi_b3'],
          hint: "Ce que je trouve le plus difficile, c'est… — the c'est is doing real work.",
        },
        {
          speaker: 'Madame Rousseau',
          prompt: 'Vous vous entraînez tous les jours ?',
          english: 'Do you practise every day?',
          cue: 'Say when and how much.',
          island: 'fr_pourquoi',
          blocks: ['fr_pourquoi_b4'],
        },
      ],
    },
  ],

  es: [
    {
      slug: 'fiesta',
      title: 'Meeting someone at a party',
      nativeTitle: 'En una fiesta',
      setting: 'Madrid, casa de un amigo, viernes por la noche',
      brief:
        "A friend's flat in Madrid. The man by the kitchen turns and starts talking. Three of your islands answer everything he is about to ask — the only question is whether you reach the right one in time.",
      beats: [
        {
          speaker: 'Javier',
          prompt: 'Hola, soy Javier. ¿A qué te dedicas?',
          english: "Hi, I'm Javier. What do you do?",
          cue: 'Say what you do and how long you have done it.',
          island: 'es_trabajo',
          blocks: ['es_trabajo_b1', 'es_trabajo_b2'],
          hint: 'llevar + time + gerund. "He trabajado por cinco años" is a translation, not Spanish.',
        },
        {
          speaker: 'Javier',
          prompt: 'Qué interesante. Hablas muy bien español — ¿cuánto tiempo llevas estudiando?',
          english: 'How interesting. You speak Spanish very well — how long have you been studying?',
          cue: 'Give the length of time, then why you started.',
          island: 'es_porque',
          blocks: ['es_porque_b1', 'es_porque_b2'],
        },
        {
          speaker: 'Javier',
          prompt: '¿Has venido solo? ¿Tienes familia aquí?',
          english: 'Did you come alone? Do you have family here?',
          cue: 'Account for your household.',
          island: 'es_familia',
          blocks: ['es_familia_b1', 'es_familia_b2'],
        },
        {
          speaker: 'Javier',
          prompt: '¿Y trabajas en una oficina o desde casa?',
          english: 'And do you work in an office or from home?',
          cue: 'Say where you usually work.',
          island: 'es_trabajo',
          blocks: ['es_trabajo_b4'],
        },
      ],
    },
    {
      slug: 'restaurante',
      title: 'Dinner for two',
      nativeTitle: 'En el restaurante',
      setting: 'Un restaurante lleno, sábado, las nueve y media',
      brief:
        'A full restaurant at half past nine. The waiter is moving fast and will not slow down. Every beat is a sentence you already have — this is a speed test, not a comprehension one.',
      beats: [
        {
          speaker: 'Camarero',
          prompt: 'Buenas noches. ¿Cuántos son?',
          english: 'Good evening. How many are you?',
          cue: 'Answer in one breath.',
          island: 'es_restaurante',
          blocks: ['es_restaurante_b1'],
        },
        {
          speaker: 'Camarero',
          prompt: 'Síganme, por favor. Ahora les tomo nota.',
          english: 'Follow me, please. I will take your order shortly.',
          cue: 'You have nothing to order from yet.',
          island: 'es_restaurante',
          blocks: ['es_restaurante_b2'],
          hint: 'la carta, not el menú — el menú is the set meal.',
        },
        {
          speaker: 'Camarero',
          prompt: '¿Qué van a tomar?',
          english: 'What are you going to have?',
          cue: 'Order a dish.',
          island: 'es_restaurante',
          blocks: ['es_restaurante_b3'],
        },
        {
          speaker: 'Camarero',
          prompt: 'Muy bien. ¿Alguna alergia o algo que no quieran?',
          english: 'Very good. Any allergy, or anything you would rather not have?',
          cue: 'Leave something out.',
          island: 'es_restaurante',
          blocks: ['es_restaurante_b4'],
        },
        {
          speaker: 'Camarero',
          prompt: '¿Algo más?',
          english: 'Anything else?',
          cue: 'Close it out.',
          island: 'es_restaurante',
          blocks: ['es_restaurante_b5'],
        },
      ],
    },
    {
      slug: 'tren',
      title: 'Small talk on the train',
      nativeTitle: 'En el tren',
      setting: 'AVE a Sevilla, dos horas y media, el asiento de enfrente se ocupa',
      brief:
        'Two and a half hours to Seville and the woman opposite has decided to talk. She uses usted throughout, as a stranger a generation older would — your answers are first person either way, so the island still fits.',
      beats: [
        {
          speaker: 'Señora Ortega',
          prompt: 'Perdone — ¿está aprendiendo español? ¿Por qué?',
          english: 'Excuse me — are you learning Spanish? Why?',
          cue: 'Say how it started.',
          island: 'es_porque',
          blocks: ['es_porque_b2'],
        },
        {
          speaker: 'Señora Ortega',
          prompt: '¿Y qué le trae a Sevilla? ¿El trabajo?',
          english: 'And what brings you to Seville? Work?',
          cue: 'Say what you do.',
          island: 'es_trabajo',
          blocks: ['es_trabajo_b1', 'es_trabajo_b2'],
        },
        {
          speaker: 'Señora Ortega',
          prompt: '¿Tiene familia aquí en España?',
          english: 'Do you have family here in Spain?',
          cue: 'Account for your household.',
          island: 'es_familia',
          blocks: ['es_familia_b1', 'es_familia_b2'],
        },
        {
          speaker: 'Señora Ortega',
          prompt: '¿Qué le resulta más difícil?',
          english: 'What do you find hardest?',
          cue: 'Name the hard part.',
          island: 'es_porque',
          blocks: ['es_porque_b3'],
        },
        {
          speaker: 'Señora Ortega',
          prompt: '¿Y practica todos los días?',
          english: 'And do you practise every day?',
          cue: 'Say when and how much.',
          island: 'es_porque',
          blocks: ['es_porque_b4'],
        },
      ],
    },
  ],

  it: [
    {
      slug: 'festa',
      title: 'Meeting someone at a party',
      nativeTitle: 'A una festa',
      setting: 'Milano, casa di un amico, venerdì sera',
      brief:
        "A friend's flat in Milan. The man by the kitchen turns and starts talking. Three of your islands answer everything he is about to ask — the only question is whether you reach the right one in time.",
      beats: [
        {
          speaker: 'Marco',
          prompt: 'Ciao, sono Marco. Che lavoro fai?',
          english: "Hi, I'm Marco. What work do you do?",
          cue: 'Say what you do and how long you have done it.',
          island: 'it_lavoro',
          blocks: ['it_lavoro_b1', 'it_lavoro_b2'],
          hint: 'fare il/lo + job is the everyday way to answer this.',
        },
        {
          speaker: 'Marco',
          prompt: 'Interessante. Parli bene italiano — da quanto lo studi?',
          english: 'Interesting. You speak Italian well — how long have you been studying it?',
          cue: 'Give the length of time, then why you started.',
          island: 'it_perche',
          blocks: ['it_perche_b1', 'it_perche_b2'],
        },
        {
          speaker: 'Marco',
          prompt: 'Sei venuto da solo? Hai famiglia qui?',
          english: 'Did you come alone? Do you have family here?',
          cue: 'Account for your household.',
          island: 'it_famiglia',
          blocks: ['it_famiglia_b1', 'it_famiglia_b2'],
        },
        {
          speaker: 'Marco',
          prompt: 'E lavori in ufficio o da casa?',
          english: 'And do you work in an office or from home?',
          cue: 'Say where you usually work.',
          island: 'it_lavoro',
          blocks: ['it_lavoro_b4'],
        },
      ],
    },
    {
      slug: 'ristorante',
      title: 'Dinner for two',
      nativeTitle: 'Al ristorante',
      setting: 'Una trattoria piena, sabato sera, le nove',
      brief:
        'A full trattoria at nine on a Saturday. The waiter is moving fast and will not slow down. Every beat is a sentence you already have — this is a speed test, not a comprehension one.',
      beats: [
        {
          speaker: 'Cameriere',
          prompt: 'Buonasera. In quanti siete?',
          english: 'Good evening. How many of you are there?',
          cue: 'Answer in one breath.',
          island: 'it_ristorante',
          blocks: ['it_ristorante_b1'],
        },
        {
          speaker: 'Cameriere',
          prompt: 'Prego, da questa parte. Vi lascio guardare.',
          english: 'This way, please. I will let you look.',
          cue: 'You have nothing to look at yet.',
          island: 'it_ristorante',
          blocks: ['it_ristorante_b2'],
        },
        {
          speaker: 'Cameriere',
          prompt: 'Avete scelto?',
          english: 'Have you chosen?',
          cue: 'Order a dish.',
          island: 'it_ristorante',
          blocks: ['it_ristorante_b3'],
        },
        {
          speaker: 'Cameriere',
          prompt: 'Benissimo. Allergie, preferenze?',
          english: 'Very good. Allergies, preferences?',
          cue: 'Leave something out.',
          island: 'it_ristorante',
          blocks: ['it_ristorante_b4'],
        },
        {
          speaker: 'Cameriere',
          prompt: 'Altro?',
          english: 'Anything else?',
          cue: 'Close it out.',
          island: 'it_ristorante',
          blocks: ['it_ristorante_b5'],
        },
      ],
    },
    {
      slug: 'treno',
      title: 'Small talk on the train',
      nativeTitle: 'In treno',
      setting: 'Frecciarossa per Bologna, un’ora, il posto di fronte si libera',
      brief:
        'An hour to Bologna and the woman opposite has decided to talk. She uses Lei throughout, as a stranger would — your answers are first person either way, so the island still fits.',
      beats: [
        {
          speaker: 'Signora Conti',
          prompt: 'Scusi — sta studiando l’italiano? Come mai?',
          english: 'Excuse me — are you studying Italian? How come?',
          cue: 'Say how it started.',
          island: 'it_perche',
          blocks: ['it_perche_b2'],
        },
        {
          speaker: 'Signora Conti',
          prompt: 'E cosa la porta a Bologna? Il lavoro?',
          english: 'And what brings you to Bologna? Work?',
          cue: 'Say what you do.',
          island: 'it_lavoro',
          blocks: ['it_lavoro_b1', 'it_lavoro_b2'],
        },
        {
          speaker: 'Signora Conti',
          prompt: 'Ha famiglia qui in Italia?',
          english: 'Do you have family here in Italy?',
          cue: 'Account for your household.',
          island: 'it_famiglia',
          blocks: ['it_famiglia_b1', 'it_famiglia_b2'],
        },
        {
          speaker: 'Signora Conti',
          prompt: 'Cosa trova più difficile?',
          english: 'What do you find hardest?',
          cue: 'Name the hard part.',
          island: 'it_perche',
          blocks: ['it_perche_b3'],
        },
        {
          speaker: 'Signora Conti',
          prompt: 'E si esercita tutti i giorni?',
          english: 'And do you practise every day?',
          cue: 'Say when and how much.',
          island: 'it_perche',
          blocks: ['it_perche_b4'],
        },
      ],
    },
  ],
}
